/* Thin wrapper around the Meta Pixel global (`window.fbq`, loaded by the
   base snippet in public/index.html). Every call is guarded so a blocked
   pixel (ad blocker, consent not granted, script still loading) can never
   throw and break the real checkout/profile flows it's attached to.

   PageView is handled two ways on purpose:
   - The base snippet in index.html fires one `fbq('track', 'PageView')`
     synchronously on first load, before React even mounts.
   - This is a client-side-routed app (react-router), so a URL change after
     that (e.g. Home -> /comprar) never reloads the page and never retriggers
     the snippet's own PageView. <MetaPixelRouteTracker/> (mounted once in
     App.js) calls trackPageView() on every route change AFTER the first one,
     so each route is counted exactly once — no double count on first load,
     no missed count on client-side navigation. */

// Hostname allowlist, not an environment check — a preview/staging deploy
// (app.emergent.sh, any *.emergentagent.com preview, localhost) can easily
// run with NODE_ENV=production, so that flag alone was never going to keep
// dev/preview traffic out of the real pixel's numbers. Checking the actual
// hostname the page is loaded from is the one thing a preview can't fake
// into matching the production domain.
const ALLOWED_HOSTS = ['contactnetwork.cl', 'www.contactnetwork.cl'];

const isAllowedHost = () => {
  try {
    return typeof window !== 'undefined' && ALLOWED_HOSTS.includes(window.location.hostname);
  } catch (e) {
    return false;
  }
};

const fbq = (...args) => {
  try {
    if (isAllowedHost() && typeof window.fbq === 'function') {
      window.fbq(...args);
    }
  } catch (e) {
    // Tracking must never break the real feature it's attached to.
  }
};

export const trackPageView = () => {
  if (!isAllowedHost()) return;
  fbq('track', 'PageView');
};

export const trackViewContent = ({ contentId, contentName, value, currency = 'CLP' } = {}) => {
  if (!isAllowedHost()) return;
  fbq('track', 'ViewContent', {
    content_ids: contentId != null ? [String(contentId)] : undefined,
    content_name: contentName,
    content_type: 'product',
    value,
    currency,
  });
};

export const trackInitiateCheckout = ({ contentId, value, currency = 'CLP', numItems } = {}) => {
  if (!isAllowedHost()) return;
  fbq('track', 'InitiateCheckout', {
    content_ids: contentId != null ? [String(contentId)] : undefined,
    content_type: 'product',
    value,
    currency,
    num_items: numItems,
  });
};

// Purchase is the one event where firing twice is a real problem (double-
// counted revenue in Ads Manager), so it's deduplicated here by
// transaction id — a reload of the success page, the user hitting back
// then forward again, or React re-running the effect all resolve to the
// same already-tracked id and this becomes a no-op. The host check runs
// before that bookkeeping too, so a preview domain never even writes the
// dedupe key for a transaction the real pixel was never meant to see.
export const trackPurchase = ({ transactionId, value, currency = 'CLP', contentId } = {}) => {
  if (!isAllowedHost()) return;
  if (!transactionId || value == null) return;
  const dedupeKey = `fb_purchase_tracked_${transactionId}`;
  try {
    if (localStorage.getItem(dedupeKey)) return;
    localStorage.setItem(dedupeKey, '1');
  } catch (e) {
    // If localStorage is unavailable (private mode, quota), fall through
    // and track anyway rather than silently losing the event.
  }
  fbq('track', 'Purchase', {
    value,
    currency,
    content_ids: contentId != null ? [String(contentId)] : undefined,
    content_type: 'product',
  });
};
